import { create } from 'zustand';

type SnackbarSeverity = 'success' | 'error' | 'warning' | 'info';

interface SnackbarMessage {
  readonly message: string;
  readonly severity: SnackbarSeverity;
  readonly duration: number;
}

interface SnackbarState {
  readonly open: boolean;
  readonly message: string;
  readonly severity: SnackbarSeverity;
  readonly duration: number;
  readonly transientSuppressed: boolean;
  readonly pendingTransient: SnackbarMessage | null;
}

interface SnackbarActions {
  readonly showSuccess: (message: string, duration?: number) => void;
  readonly showError: (message: string, duration?: number) => void;
  readonly showWarning: (message: string, duration?: number) => void;
  readonly showInfo: (message: string, duration?: number) => void;
  readonly hideSnackbar: () => void;
  readonly setTransientSuppressed: (suppressed: boolean) => void;
}

export const useSnackbarStore = create<SnackbarState & SnackbarActions>((set) => {
  const showTransient = (severity: Exclude<SnackbarSeverity, 'error'>, defaultDuration: number) =>
    (message: string, duration = defaultDuration) => set((state) => {
      const notification = { message, severity, duration };
      return state.transientSuppressed || (state.open && state.severity === 'error')
        ? { pendingTransient: notification }
        : { open: true, ...notification };
    });

  return {
    open: false,
    message: '',
    severity: 'info',
    duration: 4000,
    transientSuppressed: false,
    pendingTransient: null,

    showSuccess: showTransient('success', 4000),

    showError: (message, duration = 6000) =>
      set({ open: true, message, severity: 'error', duration, pendingTransient: null }),

    showWarning: showTransient('warning', 5000),

    showInfo: showTransient('info', 4000),

    hideSnackbar: () => set((state) => {
      if (state.open && state.severity === 'error' && state.pendingTransient) {
        if (state.transientSuppressed) {
          return { open: false };
        }

        return {
          open: true,
          pendingTransient: null,
          ...state.pendingTransient,
        };
      }

      return { open: false, pendingTransient: null };
    }),

    setTransientSuppressed: (suppressed) => set((state) => {
      if (suppressed) {
        const visibleTransient = state.open && state.severity !== 'error'
          ? { message: state.message, severity: state.severity, duration: state.duration }
          : null;
        return {
          transientSuppressed: true,
          open: visibleTransient ? false : state.open,
          pendingTransient: visibleTransient ?? state.pendingTransient,
        };
      }

      if (!state.pendingTransient) {
        return { transientSuppressed: false };
      }

      if (state.open && state.severity === 'error') {
        return { transientSuppressed: false };
      }

      return {
        transientSuppressed: false,
        pendingTransient: null,
        open: true,
        ...state.pendingTransient,
      };
    }),
  };
});
